import { sentryLog } from "../../../tools/sentry";
import { formSet } from "../../gvc/testNewForm/utils";
import { validationFns } from "../../../components/form/validations";
import { GvcInput } from "../../../schema/types/gvc/input";
import { OPERATORS_REQUIRING_TWO_VALUES } from "../../../services/utils";
import { EnvVarInput } from "../../../schema/types/workload/input";
import { parseLink } from "../../../utils/linkParser/linkParser";
import jsYaml from "js-yaml";

export function getValidator(id: string) {
  return async function validator(v: GvcInput): Promise<boolean | null> {
    // setup
    formSet(id, `meta.valid`, false);
    formSet(id, `meta.validating`, true);
    formSet(id, `errors`, {});
    formSet(id, `warnings`, {});

    const e: Partial<Record<string, { message: string }>> = {};
    const w: Partial<Record<string, { message: string }>> = {};

    function setError(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      e[path] = { message: message };
    }

    function setWarning(path: string, message: string) {
      const currentError = e[path];
      if (currentError) {
        return;
      }
      const currentWarning = w[path];
      if (currentWarning) {
        return;
      }
      w[path] = { message: message };
    }

    // setup end

    try {
      const fs = validationFns("Name");
      for (const f of [fs.required, fs.name]) {
        const r = f(v.name);
        if (r !== true) {
          setError(`name`, r);
        }
      }

      if (v.description.length > 250) {
        setError(`description`, `Description can be at most 250 characters`);
      }

      // tags start
      for (const tagIndexStr in v.tags) {
        const tagIndex = Number(tagIndexStr);
        const tag = v.tags[tagIndex];
        if (!tag.name) {
          setError(`tags.${tagIndex}.name`, "Tag key is required");
          setError(`tagsroot`, "Tag keys are required");
        }
      }

      if (v.tags.length > 50) {
        setError(`tagsroot`, "Tags cannot be more than 50 items");
      }
      // tags end

      // env start
      for (const envIndexStr in v.env) {
        const envIndex = Number(envIndexStr);
        const env = v.env[envIndex] as EnvVarInput;

        if (!env.name) {
          setError(`env.${envIndex}.name`, "Name is required");
          setError(`envroot`, "Environment variable names are required");
        }
        if (!env.prefix) {
          if (env.text.startsWith(" ") || env.text.endsWith(" ")) {
            setWarning(`env.${envIndex}.text`, "Value starts or ends with empty character");
            setWarning(`envroot`, "Some values start or end with empty character");
          }
        }
        if (env.prefix === "cpln://secret/") {
          if (!env.cplnSecret.value) {
            setError(`env.${envIndex}.cplnSecret`, "Secret value is required");
            setError(`envroot`, "Secret values are required");
          }
          if (env.cplnSecret.value.includes(" ")) {
            setWarning(`env.${envIndex}.cplnSecret`, "Value contains empty character");
            setWarning(`envroot`, "Some values contain empty character");
          }
          if (env.suffix.includes(" ")) {
            setWarning(`env.${envIndex}.suffix`, "Suffix contains empty character");
            setWarning(`envroot`, "Some suffix values contain empty character");
          }
        }
        if (env.prefix === "k8s://secret/") {
          if (!env.k8sSecret) {
            setError(`env.${envIndex}.k8sSecret`, "K8s Secret value is required");
            setError(`envroot`, "K8s Secret values are required");
          }
          if (env.k8sSecret.includes(" ")) {
            setWarning(`env.${envIndex}.k8sSecret`, "Value contains empty character");
            setWarning(`envroot`, "Some values contain empty character");
          }
        }
        if (env.prefix === "cpln://reference/") {
          if (!env.reference) {
            setError(`env.${envIndex}.reference`, "Reference value is required");
            setError(`envroot`, "Reference values are required");
          }
          if (env.reference.includes(" ")) {
            setWarning(`env.${envIndex}.reference`, "Value contains empty character");
            setWarning(`envroot`, "Some values contain empty character");
          }
        }

        for (const _envIndexStr in v.env) {
          const _envIndex = Number(_envIndexStr);
          if (_envIndex === envIndex) {
            continue;
          }

          const _env = v.env[_envIndex] as EnvVarInput;
          if (_env.name === env.name) {
            setWarning(`env.${envIndex}.name`, "Name is not unique");
            setWarning(`envroot`, "Some names are not unique");
          }
        }
      }
      // env end

      if (v.loadBalancer.isEnabled) {
        if (v.loadBalancer.ipSet.value) {
          const parsedLink = parseLink(v.loadBalancer.ipSet.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`loadBalancer.ipSet`, parsedLink.errorMessage);
          }
        }
      }

      if (v.keda.isEnabled) {
        if (v.keda.identity.value) {
          const parsedLink = parseLink(v.keda.identity.link, { useInputCtx: true });
          if (!parsedLink.isValid) {
            setError(`keda.identity`, parsedLink.errorMessage);
          }
        }
      }

      let xn: number;
      if (v.tracing.isEnabled) {
        if (!v.tracing.sampling) {
          setError(`tracing.sampling`, "Sampling is required");
        } else {
          xn = Number(v.tracing.sampling);
          if (xn < 0) {
            setError(`tracing.sampling`, "Sampling must be at least 0");
          }
          if (xn > 100) {
            setError(`tracing.sampling`, "Sampling can be at most 100");
          }
        }

        // custom tags start
        for (const indexStr in v.tracing.customTags) {
          const index = Number(indexStr);
          const tag = v.tracing.customTags[index];
          if (!tag.name) {
            setError(`tracing.customTags.${index}.name`, "Tag key is required");
            setError(`tracing.customTagsroot`, "Tag keys are required");
          }
          if (tag.value.length > 50) {
            setError(`tracing.customTags.${index}.value`, "Tag value can be at most 50 characters");
            setError(`tracing.customTagsroot`, "Tag values can be at most 50 characters");
          }
        }
        // custom tags end

        if (v.tracing.provider === "otel") {
          if (!v.tracing.otel.endpoint) {
            setError(`tracing.otel.endpoint`, "Endpoint is required");
          }
        }
        if (v.tracing.provider === "lightstep") {
          if (!v.tracing.lightstep.endpoint) {
            setError(`tracing.lightstep.endpoint`, "Endpoint is required");
          }
        }
      }

      if (v.sidecar.envoy.type === "jwt") {
        if (!v.sidecar.envoy.jwt.name) {
          setError(`sidecar.envoy.jwt.name`, "Name is required");
        }
        if (!v.sidecar.envoy.jwt.issuer) {
          setError(`sidecar.envoy.jwt.issuer`, "Issuer is required");
        }
        if (!v.sidecar.envoy.jwt.remote_jwks_http_uri) {
          setError(`sidecar.envoy.jwt.remote_jwks_http_uri`, "JWKS HTTP URI is required");
        }
        if (!v.sidecar.envoy.jwt.remote_jwks_http_uri_timeout) {
          setError(`sidecar.envoy.jwt.remote_jwks_http_uri_timeout`, "Timeout is required");
        }

        for (const indexStr in v.sidecar.envoy.jwt.rules) {
          const index = Number(indexStr);
          const rule = v.sidecar.envoy.jwt.rules[index];
          if (!rule.prefix) {
            setError(`sidecar.envoy.jwt.rules.${index}.prefix`, "Path is required");
          }
          for (const headerIndexStr in rule.headers) {
            const headerIndex = Number(headerIndexStr);
            const header = rule.headers[headerIndex];
            if (!header.name) {
              setError(`sidecar.envoy.jwt.rules.${index}.headers.${headerIndex}.name`, "Header name is required");
              setError(`sidecar.envoy.jwt.rules.${index}.headersroot`, "Header names are required");
            }
          }
        }
      }

      if (v.sidecar.envoy.type === "custom") {
        if (!v.sidecar.envoy.custom) {
          setError(`sidecar.envoy.custom`, "Custom envoy configuration is required");
        } else {
          try {
            jsYaml.load(v.sidecar.envoy.custom);
          } catch (e: any) {
            setError(`sidecar.envoy.custom`, `Invalid YAML: ${e.message}`);
          }
        }
      }

      if (v.locationQuery.isEnabled) {
        for (const indexStr in v.locationQuery.terms) {
          const index = Number(indexStr);
          const term = v.locationQuery.terms[index];
          if (!term.key) {
            setError(`locationQuery.terms.${index}.key`, "Key is required");
            setError(`locationQuery.termsroot`, "Keys are required");
          }
          if (OPERATORS_REQUIRING_TWO_VALUES.includes(term.op) && !term.value) {
            setError(`locationQuery.terms.${index}.value`, "Value is required");
            setError(`locationQuery.termsroot`, `Values are required unless Op is "exists" or "not exists"`);
          }
        }
      }

      // locationOptions validation
      for (const optIndexStr in v.locationOptions) {
        const optIndex = Number(optIndexStr);
        const opt = v.locationOptions[optIndex];

        if (!opt.locationLink) {
          setError(`locationOptions.${optIndex}.locationLink`, "Location is required");
          setError(`locationOptionsroot`, "All location options must have a location selected");
        }

        if (opt.routingTier === "") {
          setError(`locationOptions.${optIndex}.routingTier`, "Routing Tier is required");
          setError(`locationOptionsroot`, "All location options must have a routing tier");
        } else {
          const n = Number(opt.routingTier);
          if (isNaN(n) || !Number.isInteger(n)) {
            setError(`locationOptions.${optIndex}.routingTier`, "Must be an integer");
          } else if (n < 0) {
            setError(`locationOptions.${optIndex}.routingTier`, "Must be at least 0");
          }
        }

        if (opt.latencyOffsetMs !== "") {
          const n = Number(opt.latencyOffsetMs);
          if (isNaN(n) || !Number.isInteger(n)) {
            setError(`locationOptions.${optIndex}.latencyOffsetMs`, "Must be an integer");
          }
        }

        if (opt.latencyToleranceMs !== "") {
          const n = Number(opt.latencyToleranceMs);
          if (isNaN(n) || !Number.isInteger(n)) {
            setError(`locationOptions.${optIndex}.latencyToleranceMs`, "Must be an integer");
          } else if (n < 0) {
            setError(`locationOptions.${optIndex}.latencyToleranceMs`, "Must be at least 0");
          }
        }
      }

      // locationOptions uniqueness check
      const seenOptionLinks = new Set<string>();
      for (const optIndexStr in v.locationOptions) {
        const optIndex = Number(optIndexStr);
        const link = v.locationOptions[optIndex].locationLink;
        if (link && seenOptionLinks.has(link)) {
          setError(`locationOptions.${optIndex}.locationLink`, "Duplicate location");
          setError(`locationOptionsroot`, "Each location can only appear once");
        }
        if (link) seenOptionLinks.add(link);
      }

      // setup
      for (const path of Object.keys(e)) {
        const value = e[path];
        formSet(id, `errors.${path}`, value);
      }
      for (const path of Object.keys(w)) {
        const value = w[path];
        formSet(id, `warnings.${path}`, value);
      }

      const hasErrors = Object.keys(e).length > 0;

      formSet(id, `meta.valid`, !hasErrors);
      formSet(id, `meta.validating`, false);

      return hasErrors;
    } catch (e) {
      sentryLog("gvc validator error", { "data.error": e.message });
      formSet(id, `errors`, {
        root: { message: "An unexpected error occurred. This has been logged and we will fix it." },
      });
      return true;
    }
  };
}
